import type { OverlayBackdropProps } from "@/components/modal/overlay.types";
import { Transition } from "@headlessui/react";
import { Fragment, useRef } from "react";
import { createPortal } from "react-dom";

export function OverlayBackdrop({ entries, root }: OverlayBackdropProps) {
  const backdropIndex = entries.findLastIndex((entry) => entry.blocking);
  const backdropOwner = entries[backdropIndex];
  const appearance = useRef<{ className: string; zIndex: number } | null>(null);
  if (backdropOwner) {
    // An inheriting owner borrows the nearest lower blocking layer's class so stacking never lightens the page.
    const classOwner =
      (backdropOwner.inheritBackdrop &&
        entries
          .slice(0, backdropIndex)
          .findLast((entry) => entry.blocking && !entry.inheritBackdrop)) ||
      backdropOwner;
    appearance.current = {
      className: classOwner.backdropClassName ?? "bg-zinc-950/70 backdrop-blur-sm",
      zIndex: backdropIndex * 2 + 1,
    };
  }
  // Retain the last surface while the final dialog's backdrop fades out.
  if (!root || !appearance.current) return null;
  return createPortal(
    <Transition
      appear
      show={entries.some((entry) => entry.blocking && !entry.closing)}
      as={Fragment}
      enter="transition-opacity ease-out duration-[120ms]"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity ease-in duration-[120ms]"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        data-overlay-backdrop=""
        aria-hidden="true"
        className={appearance.current.className}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: appearance.current.zIndex,
        }}
      />
    </Transition>,
    root,
  );
}
